import { Text } from "@carbon6/c6-frontend-design";
import type { ReactNode } from "react";
import { hatcheryTextModes } from "@/app/theme";
import { changedAxes, type DiffAxis } from "./diffAxis";
import styles from "./DiffView.module.css";

/**
 * Before|After 표시. 축 목록을 `before → after` 로 그린다.
 *
 * 충돌 비교 화면(내 변경 / 서버 변경), 저장 전 변경 요약, 데이터셋 정보 편집이 같은 표현을
 * 쓴다. 화면마다 표현이 다르면 사용자가 변경 표시를 화면마다 다시 배워야 한다.
 */
export function DiffRows({
  axes,
  // 바뀌지 않은 축도 "변경 없음" 으로 표시할지 정한다. 충돌 비교는 축 세 개를 항상 보여 주어
  // 바뀌지 않은 축도 알려야 한다. 저장 전 요약은 바뀐 축만 모으면 된다.
  showUnchanged = false,
}: {
  axes: readonly DiffAxis[];
  showUnchanged?: boolean;
}): ReactNode {
  const rows = showUnchanged ? axes : changedAxes(axes);
  return (
    <>
      {rows.map((row) => (
        <div key={row.axis} className={styles.row}>
          <Text variant={hatcheryTextModes["caption-bold"]}>{row.axis}</Text>
          {row.changed ? (
            <Text variant={hatcheryTextModes["body-normal"]}>
              <span className={styles.muted}>{row.before}</span> → {row.after}
            </Text>
          ) : (
            <Text variant={hatcheryTextModes["body-normal"]} className={styles.muted}>
              변경 없음
            </Text>
          )}
        </div>
      ))}
    </>
  );
}

/** 저장 전 변경 요약. 바뀐 축이 없으면 공간을 차지하지 않는다. */
export function ChangeSummary({
  title,
  axes,
  columns,
}: {
  title: string;
  axes: readonly DiffAxis[];
  // 두 값에 각각 이름을 붙인다. 예: `이전` → `변경 요청`. 생략하면 화살표만 표시한다.
  // 이름은 두 값의 성격이 다른 곳(적용된 값과 보낼 값)에서만 필요하다.
  columns?: { before: string; after: string };
}): ReactNode {
  const changed = changedAxes(axes);
  if (changed.length === 0) {
    return null;
  }
  return (
    <div className={styles.changeSummary} aria-label={title}>
      <Text variant={hatcheryTextModes["caption-bold"]} className={styles.changeSummaryHead}>
        {title}
      </Text>
      {columns === undefined ? null : (
        <Text variant={hatcheryTextModes["caption-normal"]} className={styles.muted}>
          {`${columns.before} → ${columns.after}`}
        </Text>
      )}
      <DiffRows axes={changed} />
    </div>
  );
}

/** 바뀐 필드 옆의 표시. 값이 같으면 아무것도 그리지 않는다. */
export function ModifiedMark({ changed }: { changed: boolean }): ReactNode {
  if (!changed) {
    return null;
  }
  return (
    <span className={styles.modifiedMark} aria-label="변경됨">
      변경됨
    </span>
  );
}
